import { AuthContext, AuthProvider, IAuthProvider } from "react-oauth2-code-pkce";
import { JSX, useContext, useEffect, useState } from "react";
import LoadingContent from "../LoadingContent";
import { HorizontalFlex, LoaderSize } from "@enxcs/dap-ui-stratos";
import { useDuplicateTabGuard } from "@/hooks/useDuplicateTabGuard";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import { useUserInfoPreload } from "@/hooks/useUserInfoPreLoad";
import { useBlockedUser } from "@/hooks/useBlockedUser";

type Props = IAuthProvider;

function LoginHelper({ children }: { children: React.ReactNode }): JSX.Element {
  const [isBlocking, setIsBlocking] = useState(true);
  const { logOut } = useContext(AuthContext);

  const { tabValidated } = useDuplicateTabGuard();
  const { token, loginInProgress } = useAuthRedirect();
  const { userInfoLoading } = useUserInfoPreload(token, loginInProgress);
  const isBlocked = useBlockedUser(token);

  const shouldBlock = loginInProgress || !tabValidated || userInfoLoading || isBlocked;

  useEffect(() => {
    if (isBlocked) {
      const redirectURL = import.meta.env.VITE_IBABS_PORTAL_URL || "https://portal.ibabs.eu";
      logOut(undefined, undefined, { returnUrl: `${redirectURL}/Error.htm` });
    }
  }, [isBlocked, logOut]);

  useEffect(() => {
    if (shouldBlock) {
      setIsBlocking(true);
      return;
    }

    const timeout = setTimeout(() => {
      setIsBlocking(false);
    }, 200);

    return () => clearTimeout(timeout);
  }, [shouldBlock]);

  if (isBlocking) {
    return (
      <HorizontalFlex $grow $justify="center" $align="center" style={{ height: "100vh" }}>
        <LoadingContent loaderSize={LoaderSize.V32} loaderHeight={LoaderSize.V56} loaderWidth={LoaderSize.V56} />
      </HorizontalFlex>
    );
  }

  return <>{children}</>;
}

const Authenticator = ({ authConfig, children }: Props) => (
  <AuthProvider authConfig={authConfig}>
    <LoginHelper>{children}</LoginHelper>
  </AuthProvider>
);

export default Authenticator;
